import type { TypeUploadConfig, TypeResolvedFeatures } from '../types';

// ----------------------------------------------------------------------------

const DEFAULT_RETRY_BASE_MS = 300;
const DEFAULT_PROGRESS_THROTTLE_MS = 80;
const DEFAULT_CONCURRENCY = 4;
const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024;

// ----------------------------------------------------------------------------

/*
 * Numbers reaching here come from caller config, so a non-numeric value must not become NaN and
 * silently disable or unbound a feature. Anything unparseable falls back to the default.
 */
function toNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : fallback;
}

/*
 * `enabled` is honoured when the caller states it, and inferred from the feature's own values
 * otherwise — so `{ retry: { max: 3 } }` and the older `{ maxRetries: 3 }` both switch retry on
 * without a flag, and neither switches it on when the value is zero or missing.
 */
function isEnabled(explicit: boolean | undefined, inferred: boolean): boolean {
    return explicit ?? inferred;
}

// ----------------------------------------------------------------------------

/*
 * Turns a caller's config into one filled-in feature block per feature, which is the only shape
 * the pipeline reads. This is also the single place the previous flat keys are understood: a
 * block wins over its flat equivalent, and everything downstream sees blocks only.
 */
function resolveUploadConfig(config: TypeUploadConfig): TypeResolvedFeatures {
    // `concurrency: 4` is shorthand for `concurrency: { max: 4 }`.
    const concurrency = typeof config.concurrency === 'object' ? config.concurrency : undefined;
    const concurrencyShorthand = typeof config.concurrency === 'number' ? config.concurrency : undefined;

    const retryMax = Math.max(0, toNumber(config.retry?.max ?? config.maxRetries, 0));
    const stallTimeoutMs = Math.max(0, toNumber(config.stall?.timeoutMs ?? config.stallTimeoutMs, 0));
    const timeoutMs = Math.max(0, toNumber(config.timeout?.ms ?? config.timeoutMs, 0));
    const concurrencyMax = Math.max(
        1,
        toNumber(concurrency?.max ?? concurrencyShorthand ?? config.maxConcurrency, DEFAULT_CONCURRENCY),
    );
    const chunkSize = Math.max(1, toNumber(config.chunk?.size, DEFAULT_CHUNK_SIZE));

    return {
        retry: {
            enabled: isEnabled(config.retry?.enabled, retryMax > 0),
            max: retryMax,
            baseMs: Math.max(0, toNumber(config.retry?.baseMs ?? config.retryBaseMs, DEFAULT_RETRY_BASE_MS)),
            fnIsRetryable: config.retry?.fnIsRetryable,
        },
        stall: {
            enabled: isEnabled(config.stall?.enabled, stallTimeoutMs > 0),
            timeoutMs: stallTimeoutMs,
        },
        timeout: {
            enabled: isEnabled(config.timeout?.enabled, timeoutMs > 0),
            ms: timeoutMs,
        },
        /*
         * Unlike the others this one defaults to on: an upload with no cap at all would open one
         * request per file at once. `enabled: false` is the explicit way to ask for that.
         */
        concurrency: {
            enabled: isEnabled(concurrency?.enabled, true),
            max: concurrencyMax,
            gateFirst: concurrency?.gateFirst ?? config.gateFirst ?? false,
            stopOnError: concurrency?.stopOnError ?? config.stopOnError ?? false,
        },
        progress: {
            enabled: isEnabled(config.progress?.enabled, true),
            throttleMs: Math.max(
                0,
                toNumber(config.progress?.throttleMs ?? config.progressThrottleMs, DEFAULT_PROGRESS_THROTTLE_MS),
            ),
        },
        filter: {
            enabled: isEnabled(config.filter?.enabled, typeof (config.filter?.fn ?? config.fnFilter) === 'function'),
            fn: config.filter?.fn ?? config.fnFilter,
        },
        chunk: {
            enabled: isEnabled(config.chunk?.enabled, false),
            size: chunkSize,
            // A file that fits in one chunk gains nothing from being split, so this defaults to size.
            minSize: Math.max(1, toNumber(config.chunk?.minSize, chunkSize)),
            fnHeaders: config.chunk?.fnHeaders,
            fnChunkBody: config.chunk?.fnChunkBody,
        },
    };
}

// ----------------------------------------------------------------------------

export default resolveUploadConfig;
